/**
 * Error code mapping for API endpoints
 * Maps API paths to human-friendly error codes
 */

interface ErrorCodeMapping {
  pattern: RegExp;
  method?: string | string[]; // Optional HTTP method(s) to match
  code: string;
}

/**
 * Error code mappings for all API endpoints
 * Codes follow the format: <RESOURCE>_<OPERATION>
 */
const ERROR_CODE_MAPPINGS: ErrorCodeMapping[] = [
  // Authentication
  { pattern: /^\/auth\/login/, method: 'POST', code: 'API_AUTH_LOGIN' },
  { pattern: /^\/auth\/logout/, method: 'POST', code: 'API_AUTH_LOGOUT' },
  {
    pattern: /^\/auth\/refresh/,
    method: 'POST',
    code: 'API_AUTH_REFRESH',
  },

  // Programs
  // Note: /programs/my must come before /programs/[^/]+$ to match correctly
  { pattern: /^\/programs\/my$/, method: 'GET', code: 'API_PROGRAM_LIST' },
  {
    pattern: /^\/programs\/[^/]+$/,
    method: 'GET',
    code: 'API_PROGRAM_GET',
  },
  {
    pattern: /^\/programs\/[^/]+$/,
    method: 'PATCH',
    code: 'API_PROGRAM_UPDATE',
  },
  {
    pattern: /^\/programs\/[^/]+$/,
    method: 'DELETE',
    code: 'API_PROGRAM_DELETE',
  },
  {
    pattern: /^\/programs$/,
    method: 'POST',
    code: 'API_PROGRAM_CREATE',
  },
  {
    pattern: /^\/programs\/[^/]+\/caserecords\/download/,
    method: 'GET',
    code: 'API_PROGRAM_DOWNLOAD',
  },

  // Workflows
  {
    pattern: /^\/workflows\/[^/]+\/caserecords\/counts/,
    method: 'GET',
    code: 'API_WORKFLOW_COUNTS',
  },
  {
    pattern: /^\/workflows\/[^/]+\/caserecords/,
    method: 'GET',
    code: 'API_WORKFLOW_CASES',
  },
  {
    pattern: /^\/workflows\/[^/]+$/,
    method: 'GET',
    code: 'API_WORKFLOW_GET',
  },
  {
    pattern: /^\/workflows\/[^/]+$/,
    method: 'PATCH',
    code: 'API_WORKFLOW_UPDATE',
  },
  {
    pattern: /^\/workflows\/[^/]+$/,
    method: 'DELETE',
    code: 'API_WORKFLOW_DELETE',
  },
  {
    pattern: /^\/workflows$/,
    method: 'GET',
    code: 'API_WORKFLOW_LIST',
  },
  {
    pattern: /^\/workflows$/,
    method: 'POST',
    code: 'API_WORKFLOW_CREATE',
  },

  // Case Records
  {
    pattern: /^\/caserecords\/[^/]+\/mark-as-ae/,
    method: 'POST',
    code: 'API_CASE_MARK_AS_AE',
  },
  {
    pattern: /^\/caserecords\/[^/]+\/manual\/actions\/link-to-case/,
    method: 'POST',
    code: 'API_CASE_LINK_TO_CASE',
  },
  {
    pattern: /^\/caserecords\/[^/]+\/action/,
    method: 'POST',
    code: 'API_CASE_ACTION',
  },
  {
    pattern: /^\/caserecords\/[^/]+\/output/,
    method: 'PATCH',
    code: 'API_CASE_OUTPUT',
  },
  {
    pattern: /^\/caserecords\/[^/]+\/input/,
    method: 'PATCH',
    code: 'API_CASE_INPUT',
  },
  {
    pattern: /^\/caserecords\/[^/]+\/assign/,
    method: 'PATCH',
    code: 'API_CASE_ASSIGN',
  },
  {
    pattern: /^\/caserecords\/[^/]+\/cancel/,
    method: 'PATCH',
    code: 'API_CASE_CANCEL',
  },
  {
    pattern: /^\/caserecords\/[^/]+\/assets/,
    method: 'GET',
    code: 'API_CASE_ASSETS',
  },
  {
    pattern: /^\/caserecords\/search/,
    method: 'POST',
    code: 'API_CASE_SEARCH',
  },
  {
    pattern: /^\/caserecords\/[^/]+$/,
    method: 'GET',
    code: 'API_CASE_GET',
  },
  {
    pattern: /^\/caserecords\/[^/]+$/,
    method: 'PATCH',
    code: 'API_CASE_UPDATE',
  },
  {
    pattern: /^\/caserecords\/[^/]+$/,
    method: 'DELETE',
    code: 'API_CASE_DELETE',
  },
  {
    pattern: /^\/caserecords$/,
    method: 'POST',
    code: 'API_CASE_CREATE',
  },

  // Sandbox Case Records (test mode)
  {
    pattern: /^\/sandbox\/caserecords/,
    method: ['GET', 'POST', 'PATCH'],
    code: 'API_CASE_SANDBOX',
  },
  {
    pattern: /^\/sandbox\/workflows/,
    method: 'GET',
    code: 'API_WORKFLOW_SANDBOX',
  },

  // Generic Tasks
  {
    pattern: /^\/organizations\/[^/]+\/programs\/[^/]+\/caserecords\/[^/]+\/genericTask\/[^/]+$/,
    method: 'GET',
    code: 'API_TASK_GET',
  },
  {
    pattern: /^\/organizations\/[^/]+\/programs\/[^/]+\/caserecords\/[^/]+\/genericTask\/[^/]+$/,
    method: 'PATCH',
    code: 'API_TASK_UPDATE',
  },
  {
    pattern: /^\/organizations\/[^/]+\/programs\/[^/]+\/caserecords\/[^/]+\/genericTask$/,
    method: 'GET',
    code: 'API_TASK_LIST',
  },
  {
    pattern: /^\/organizations\/[^/]+\/programs\/[^/]+\/caserecords\/[^/]+\/genericTask$/,
    method: 'POST',
    code: 'API_TASK_CREATE',
  },

  // Eligibility Checks
  {
    pattern: /^\/organizations\/[^/]+\/programs\/[^/]+\/caserecords\/[^/]+\/eligibility-checks/,
    method: 'GET',
    code: 'API_CASE_ELIGIBILITY_CHECKS',
  },
  {
    pattern:
      /^\/organizations\/[^/]+\/programs\/[^/]+\/caserecords\/[^/]+\/eligibility-checks\/[^/]+$/,
    method: 'GET',
    code: 'API_ELIGIBILITY_CHECK_GET',
  },
  {
    pattern:
      /^\/organizations\/[^/]+\/programs\/[^/]+\/caserecords\/[^/]+\/eligibility-checks\/[^/]+$/,
    method: 'PATCH',
    code: 'API_ELIGIBILITY_CHECK_UPDATE',
  },
  {
    pattern: /^\/organizations\/[^/]+\/programs\/[^/]+\/caserecords\/[^/]+\/eligibility-checks$/,
    method: 'POST',
    code: 'API_ELIGIBILITY_CHECK_CREATE',
  },

  // Assets
  {
    pattern: /^\/assets\/[^/]+\/download/,
    method: 'GET',
    code: 'API_ASSET_DOWNLOAD',
  },
  { pattern: /^\/assets\/[^/]+$/, method: 'GET', code: 'API_ASSET_GET' },

  // Calls
  {
    pattern: /^\/organizations\/[^/]+\/programs\/[^/]+\/caserecords\/[^/]+\/calls\/[^/]+\/token/,
    method: 'GET',
    code: 'API_CALL_TOKEN',
  },
  {
    pattern: /^\/organizations\/[^/]+\/programs\/[^/]+\/caserecords\/[^/]+\/calls\/[^/]+$/,
    method: 'GET',
    code: 'API_CALL_GET',
  },
  {
    pattern: /^\/organizations\/[^/]+\/programs\/[^/]+\/caserecords\/[^/]+\/calls\/[^/]+$/,
    method: 'PATCH',
    code: 'API_CALL_UPDATE',
  },
  {
    pattern: /^\/organizations\/[^/]+\/programs\/[^/]+\/caserecords\/[^/]+\/calls$/,
    method: 'POST',
    code: 'API_CALL_CREATE',
  },
  {
    pattern:
      /^\/organizations\/[^/]+\/programs\/[^/]+\/caserecords\/[^/]+\/calls\/[^/]+\/extract-output$/,
    method: 'POST',
    code: 'API_CALL_EXTRACT_OUTPUT',
  },
  // Note: API_CASE_CALLS must come AFTER the more specific call patterns above
  {
    pattern: /^\/organizations\/[^/]+\/programs\/[^/]+\/caserecords\/[^/]+\/calls$/,
    method: 'GET',
    code: 'API_CASE_CALLS',
  },

  // Case Records (organization-scoped path)
  // Must come AFTER more specific patterns (genericTask, eligibility-checks, calls)
  {
    pattern: /^\/organizations\/[^/]+\/programs\/[^/]+\/caserecords\/[^/]+$/,
    method: 'GET',
    code: 'API_CASE_GET',
  },

  // Campaigns
  {
    pattern: /^\/campaigns\/[^/]+\/download/,
    method: 'GET',
    code: 'API_CAMPAIGN_DOWNLOAD',
  },
  {
    pattern: /^\/campaigns\/[^/]+$/,
    method: 'GET',
    code: 'API_CAMPAIGN_GET',
  },
  {
    pattern: /^\/campaigns$/,
    method: 'GET',
    code: 'API_CAMPAIGN_LIST',
  },

  // Organizations
  {
    pattern: /^\/organizations\/my$/,
    method: 'GET',
    code: 'API_ORG_LIST',
  },
  {
    pattern: /^\/organizations\/[^/]+$/,
    method: 'GET',
    code: 'API_ORG_GET',
  },

  // Twilio Webhooks
  {
    pattern: /^\/twilio\/webhook/,
    method: 'POST',
    code: 'API_TWILIO_WEBHOOK',
  },

  // Export
  { pattern: /^\/export/, method: 'GET', code: 'API_EXPORT_DATA' },
];

/**
 * Get error code for a given API path and method
 */
export function getErrorCode(path: string, method: string = 'GET'): string {
  // Remove query parameters and API version prefix
  const cleanPath = path.split('?')[0].replace(/^\/api\/v\d+/, '');
  const methodUpper = method.toUpperCase();

  // Find matching error code
  for (const mapping of ERROR_CODE_MAPPINGS) {
    if (mapping.pattern.test(cleanPath)) {
      // If method is specified in mapping, check if it matches
      if (mapping.method) {
        const allowedMethods = Array.isArray(mapping.method)
          ? mapping.method.map(m => m.toUpperCase())
          : [mapping.method.toUpperCase()];

        if (allowedMethods.includes(methodUpper)) {
          return mapping.code;
        }
        // Method doesn't match, continue to next mapping
        continue;
      }

      // No method specified in mapping = match any method
      return mapping.code;
    }
  }

  // Fallback for unmapped endpoints
  const pathSegments = cleanPath.split('/').filter(Boolean);
  const resource = pathSegments[0]?.toUpperCase() || 'UNKNOWN';

  return `${resource}_${methodUpper}`;
}

/**
 * Extended error interface with error code
 */
export interface ApiErrorWithCode extends Error {
  code?: string;
  APIHttpStatus?: number;
  userMessage?: string; // Pre-formatted user-friendly error message
}
